import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Users,
  ArrowUpRight,
  Clock,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Wallet,
  CreditCard,
  Video,
  Phone,
  Gift,
  Disc,
  ShieldCheck,
  ShieldAlert,
  Radio,
  TrendingUp,
  RefreshCw,
} from "lucide-react";
import API from "../api/api";

const toRelativeTime = (value) => {
  if (!value) return "Just now";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Recently";
  const diffMs = Date.now() - d.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const compact = (value) => new Intl.NumberFormat("en-IN", { notation: "compact" }).format(Number(value || 0));

const formatINR = (value) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(
    Number(value || 0)
  );

export default function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const [activityTab, setActivityTab] = useState("all");
  const [leaderboardTab, setLeaderboardTab] = useState("earners");

  const fetchOverview = async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await API.get("/admin/dashboard/overview");
      if (!data?.success) throw new Error(data?.message || "Failed to fetch dashboard");
      setOverview(data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to fetch dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  const stats = useMemo(() => overview?.stats || {}, [overview?.stats]);
  const alerts = useMemo(() => overview?.actionAlerts || {}, [overview?.actionAlerts]);

  // Primary High-Impact KPIs
  const primaryKpis = useMemo(
    () => [
      {
        title: "Total Registered Users",
        value: compact(stats.totalUsers || 0),
        subtext: `+${stats.usersToday || 0} joined today`,
        secondaryInfo: `${stats.onlineUsers || 0} online now`,
        icon: Users,
        gradient: "from-blue-500 to-indigo-600",
        bgLight: "bg-blue-50 dark:bg-blue-500/10",
        textColor: "text-blue-500",
        link: "/admin/users",
      },
      {
        title: "Total Wallet Circulation",
        value: `${compact(stats.wallet?.totalBalance || 0)} coins`,
        subtext: `${compact(stats.transactionsToday || 0)} tx today`,
        secondaryInfo: `Purchased: ${compact(stats.wallet?.totalLifetimePurchased || 0)}`,
        icon: Wallet,
        gradient: "from-purple-500 to-violet-600",
        bgLight: "bg-purple-50 dark:bg-purple-500/10",
        textColor: "text-purple-500",
        link: "/admin/coin-records",
      },
      {
        title: "Total Payouts & Withdrawals",
        value: formatINR(stats.withdrawals?.approved?.amount || 0),
        subtext: `${stats.withdrawals?.pending?.count || 0} pending review`,
        secondaryInfo: `${stats.withdrawals?.today || 0} requests today`,
        icon: CreditCard,
        gradient: "from-emerald-500 to-teal-600",
        bgLight: "bg-emerald-50 dark:bg-emerald-500/10",
        textColor: "text-emerald-500",
        link: "/admin/withdrawals",
        badge:
          stats.withdrawals?.pending?.count > 0 ? `${stats.withdrawals?.pending?.count} Pending` : null,
      },
      {
        title: "Calls & Live Sessions",
        value: compact(stats.calls?.totalSessions || 0),
        subtext: `+${stats.calls?.sessionsToday || 0} today`,
        secondaryInfo: `${stats.calls?.activeCalls || 0} active in call`,
        icon: Video,
        gradient: "from-rose-500 to-pink-600",
        bgLight: "bg-rose-50 dark:bg-rose-500/10",
        textColor: "text-rose-500",
        link: "/admin/live-streaming",
        badge: stats.streams?.activeLive > 0 ? `${stats.streams?.activeLive} Live Now` : null,
      },
    ],
    [stats]
  );

  // Secondary Activity & Engagement KPIs
  const secondaryKpis = useMemo(
    () => [
      {
        title: "KYC Verification",
        primaryVal: `${stats.kyc?.approved || 0} Verified`,
        secondaryVal: `${stats.kyc?.pending || 0} Pending`,
        progress:
          stats.kyc?.total > 0
            ? Math.round(((stats.kyc?.approved || 0) / stats.kyc?.total) * 100)
            : 0,
        icon: ShieldCheck,
        color: "text-cyan-500",
        bgColor: "bg-cyan-50 dark:bg-cyan-500/10",
        link: "/admin/kyc",
      },
      {
        title: "Virtual Gifts Sent",
        primaryVal: `${compact(stats.gifts?.totalSent || 0)} Gifts`,
        secondaryVal: `${compact(stats.gifts?.totalCoins || 0)} Coins value`,
        progress: stats.gifts?.today > 0 ? 100 : 0,
        icon: Gift,
        color: "text-amber-500",
        bgColor: "bg-amber-50 dark:bg-amber-500/10",
        link: "/admin/gifts",
      },
      {
        title: "Lucky Spin Wheel",
        primaryVal: `${compact(stats.spin?.totalSpins || 0)} Spins`,
        secondaryVal: `${compact(stats.spin?.totalCoinsWon || 0)} Coins won`,
        progress: stats.spin?.today > 0 ? 100 : 0,
        icon: Disc,
        color: "text-fuchsia-500",
        bgColor: "bg-fuchsia-50 dark:bg-fuchsia-500/10",
        link: "/admin/spin-wheel",
      },
      {
        title: "Voice & Chat Rooms",
        primaryVal: `${stats.rooms?.active || 0} Active Rooms`,
        secondaryVal: `${stats.rooms?.total || 0} Total created`,
        progress: stats.rooms?.active > 0 ? 100 : 0,
        icon: Radio,
        color: "text-indigo-500",
        bgColor: "bg-indigo-50 dark:bg-indigo-500/10",
        link: "/admin/rooms",
      },
    ],
    [stats]
  );

  // Consolidated Multi-Category Unified Activity Stream
  const unifiedActivities = useMemo(() => {
    const items = [];

    // Transactions
    (overview?.recentTransactions || []).forEach((tx) => {
      items.push({
        id: `tx-${tx._id}`,
        category: "transactions",
        icon: Wallet,
        iconBg: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
        title: `${(tx.source || "coin").replace(/_/g, " ").toUpperCase()}`,
        subtitle: `${tx.fromUser?.name || "System"} -> ${tx.toUser?.name || "Platform"}`,
        amount: `${tx.amount || 0} coins`,
        amountType: "coins",
        createdAt: tx.createdAt,
        user: tx.fromUser || tx.toUser,
      });
    });

    // Call Sessions
    (overview?.recentSessions || []).forEach((sess) => {
      const isVideo = sess.type === "video";
      items.push({
        id: `sess-${sess._id}`,
        category: "calls",
        icon: isVideo ? Video : Phone,
        iconBg: "bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400",
        title: `${isVideo ? "Video Call" : "Audio Call"} (${sess.status || "ended"})`,
        subtitle: `${sess.callerId?.name || "Caller"} with ${sess.receiverId?.name || "Receiver"}`,
        amount: sess.durationSeconds ? `${Math.round(sess.durationSeconds / 60)} min` : "Call session",
        amountType: "duration",
        createdAt: sess.createdAt,
        user: sess.callerId,
      });
    });

    // Withdrawals
    (overview?.recentWithdrawals || []).forEach((w) => {
      items.push({
        id: `w-${w._id}`,
        category: "kyc_withdrawals",
        icon: CreditCard,
        iconBg:
          w.status === "approved"
            ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
            : w.status === "rejected"
            ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
            : "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
        title: `Withdrawal (${w.status?.toUpperCase() || "PENDING"})`,
        subtitle: `${w.user?.name || "User"} via ${w.paymentMethod || "UPI"}`,
        amount: formatINR(w.amount || 0),
        amountType: "money",
        createdAt: w.createdAt,
        user: w.user,
      });
    });

    // KYC Submissions
    (overview?.recentKyc || []).forEach((k) => {
      items.push({
        id: `kyc-${k._id}`,
        category: "kyc_withdrawals",
        icon: ShieldCheck,
        iconBg:
          k.status === "approved"
            ? "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400"
            : "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
        title: `KYC (${k.status?.toUpperCase() || "PENDING"})`,
        subtitle: `${k.user?.name || "User"} - ${k.idType || "ID Verification"}`,
        amount: k.status === "approved" ? "Verified" : "Under Review",
        amountType: "status",
        createdAt: k.createdAt,
        user: k.user,
      });
    });

    // Gifts
    (overview?.recentGifts || []).forEach((g) => {
      items.push({
        id: `gift-${g._id}`,
        category: "gifts",
        icon: Gift,
        iconBg: "bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400",
        title: `Gift Sent: ${g.gift?.name || "Virtual Gift"}`,
        subtitle: `${g.fromUser?.name || "Sender"} -> ${g.toUser?.name || "Receiver"}`,
        amount: `+${g.totalCoins || 0} coins`,
        amountType: "coins",
        createdAt: g.createdAt,
        user: g.fromUser,
      });
    });

    // Sort by createdAt descending
    items.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    return items;
  }, [overview]);

  // Filtered Activities
  const filteredActivities = useMemo(() => {
    if (activityTab === "all") return unifiedActivities.slice(0, 10);
    return unifiedActivities.filter((item) => item.category === activityTab).slice(0, 10);
  }, [unifiedActivities, activityTab]);

  // Total Coins in Breakdown for percentage bars
  const coinBreakdown = useMemo(() => stats?.coinBreakdown || [], [stats?.coinBreakdown]);
  const maxCoinBreakdown = useMemo(() => {
    return Math.max(...coinBreakdown.map((item) => item.totalCoins || 0), 1);
  }, [coinBreakdown]);

  // Leaderboard users
  const leaderboardUsers = useMemo(() => {
    if (leaderboardTab === "earners") {
      return overview?.topEarners || [];
    }
    return overview?.topWalletUsers || [];
  }, [overview, leaderboardTab]);

  return (
    <div className="space-y-4 lg:space-y-6 animate-fadeIn pb-8 lg:pl-5 xl:pl-7 2xl:pl-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">
              Platform Activity & Analytics
            </h1>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live Monitoring
            </span>
          </div>
          <p className="text-gray-500 dark:text-gray-400 mt-0.5 text-xs lg:text-sm">
            Real-time monitoring across coin transactions, video calls, live streams, KYC, and withdrawals
          </p>
        </div>

        <div className="flex items-center gap-2 lg:gap-3">
          <button
            onClick={fetchOverview}
            disabled={loading}
            className="flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl text-xs lg:text-sm font-semibold text-white shadow-md shadow-blue-500/20 transition-all disabled:opacity-50 cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            <span>Refresh Analytics</span>
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 px-4 py-3 text-sm rounded-xl flex items-center gap-2 border border-red-200 dark:border-red-500/20">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Actionable Urgent Alerts Banner (if items require admin action) */}
      {(alerts.pendingKyc > 0 ||
        alerts.pendingWithdrawals?.count > 0 ||
        alerts.activeLiveStreams > 0 ||
        alerts.awaitingReports > 0) && (
        <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 dark:from-amber-950/20 dark:via-orange-950/20 dark:to-rose-950/20 border border-amber-200 dark:border-amber-800/40 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />
              </span>
              <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                Action Required: Pending Platform Activities
              </h3>
            </div>
            <span className="text-[11px] font-medium text-amber-700 dark:text-amber-300">
              Needs Admin Attention
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            {alerts.pendingKyc > 0 && (
              <NavLink
                to="/admin/kyc"
                className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-800 rounded-xl border border-amber-200 dark:border-slate-700 hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldCheck className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                  <div className="truncate">
                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                      {alerts.pendingKyc} KYC Verifications
                    </p>
                    <p className="text-[10px] text-gray-500">Waiting for review</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
              </NavLink>
            )}

            {alerts.pendingWithdrawals?.count > 0 && (
              <NavLink
                to="/admin/withdrawals"
                className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-800 rounded-xl border border-amber-200 dark:border-slate-700 hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <CreditCard className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <div className="truncate">
                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                      {alerts.pendingWithdrawals?.count} Payouts ({formatINR(alerts.pendingWithdrawals?.amount)})
                    </p>
                    <p className="text-[10px] text-gray-500">Approval needed</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
              </NavLink>
            )}

            {alerts.activeLiveStreams > 0 && (
              <NavLink
                to="/admin/live-streaming"
                className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-800 rounded-xl border border-amber-200 dark:border-slate-700 hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Radio className="w-4 h-4 text-rose-500 animate-pulse flex-shrink-0" />
                  <div className="truncate">
                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                      {alerts.activeLiveStreams} Streams Live Now
                    </p>
                    <p className="text-[10px] text-gray-500">Broadcasting right now</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
              </NavLink>
            )}

            {alerts.awaitingReports > 0 && (
              <NavLink
                to="/admin/abuse-reports"
                className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-800 rounded-xl border border-amber-200 dark:border-slate-700 hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldAlert className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <div className="truncate">
                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                      {alerts.awaitingReports} Abuse Reports
                    </p>
                    <p className="text-[10px] text-gray-500">Moderation required</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
              </NavLink>
            )}
          </div>
        </div>
      )}

      {/* Primary KPI Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5">
        {primaryKpis.map((kpi, idx) => (
          <NavLink
            to={kpi.link}
            key={kpi.title}
            className="relative group bg-white dark:bg-slate-800 rounded-2xl p-4 lg:p-5 border border-gray-100 dark:border-gray-700/80 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
          >
            {/* Ambient Background Blur */}
            <div
              className={`absolute top-0 right-0 w-28 h-28 bg-gradient-to-br ${kpi.gradient} opacity-5 rounded-full -translate-y-8 translate-x-8 group-hover:scale-150 transition-transform duration-500`}
            />

            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-xl ${kpi.bgLight}`}>
                  <kpi.icon className={`w-5 h-5 ${kpi.textColor}`} />
                </div>
                {kpi.badge && (
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-rose-500 text-white animate-pulse">
                    {kpi.badge}
                  </span>
                )}
              </div>

              <h3 className="text-xl lg:text-2xl font-extrabold text-gray-900 dark:text-gray-100 tracking-tight">
                {kpi.value}
              </h3>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-0.5">{kpi.title}</p>

              <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-between text-[11px]">
                <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-0.5">
                  <ArrowUpRight className="w-3 h-3" />
                  {kpi.subtext}
                </span>
                <span className="text-gray-400 truncate">{kpi.secondaryInfo}</span>
              </div>
            </div>
          </NavLink>
        ))}
      </div>

      {/* Secondary Activity Overview Grid (KYC, Gifts, Spin, Rooms) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {secondaryKpis.map((item) => (
          <NavLink
            to={item.link}
            key={item.title}
            className="p-3.5 bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${item.bgColor} ${item.color} flex-shrink-0`}>
                <item.icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-gray-800 dark:text-gray-200 truncate">
                  {item.primaryVal}
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{item.title}</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:translate-x-1 transition-transform" />
            </div>
            <div className="mt-2.5 flex items-center justify-between text-[10px] text-gray-400">
              <span>{item.secondaryVal}</span>
              {item.progress > 0 && <span className="font-semibold text-cyan-600">{item.progress}%</span>}
            </div>
          </NavLink>
        ))}
      </div>

      {/* Analytics Core: Coin Flow Breakdown & Multi-Category Activity Feed */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
        {/* Coin Flow & Activity Distribution (1 col) */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4 lg:p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-purple-50 dark:bg-purple-500/10 text-purple-600">
                  <TrendingUp className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                    Coin Economy Flow
                  </h3>
                  <p className="text-[11px] text-gray-500">Volume distribution by activity</p>
                </div>
              </div>
              <NavLink
                to="/admin/coin-records"
                className="text-xs text-blue-600 hover:text-blue-700 font-semibold"
              >
                View Records
              </NavLink>
            </div>

            {/* Breakdown List */}
            <div className="mt-4 space-y-3">
              {coinBreakdown.length === 0 ? (
                <div className="py-8 text-center text-xs text-gray-400">
                  No coin activities recorded yet
                </div>
              ) : (
                coinBreakdown.map((item) => {
                  const sourceName = (item._id || "other").replace(/_/g, " ");
                  const percent = Math.round(((item.totalCoins || 0) / maxCoinBreakdown) * 100);

                  return (
                    <div key={item._id} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-gray-800 dark:text-gray-200 capitalize">
                          {sourceName}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-gray-400">{item.count} tx</span>
                          <span className="font-bold text-gray-900 dark:text-gray-100">
                            {compact(item.totalCoins)} coins
                          </span>
                        </div>
                      </div>
                      {/* Bar */}
                      <div className="w-full bg-gray-100 dark:bg-slate-700 h-2 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500"
                          style={{ width: `${Math.max(percent, 5)}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Quick Platform Health Summary */}
          <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 gap-2 text-center">
            <div className="p-2.5 rounded-xl bg-gray-50 dark:bg-slate-700/40">
              <p className="text-[10px] text-gray-400">Total Transactions</p>
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mt-0.5">
                {compact(stats.totalTransactions || 0)}
              </p>
            </div>
            <div className="p-2.5 rounded-xl bg-gray-50 dark:bg-slate-700/40">
              <p className="text-[10px] text-gray-400">Today's Transactions</p>
              <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                +{compact(stats.transactionsToday || 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Multi-Category Unified Live Activity Stream (2 cols) */}
        <div className="xl:col-span-2 bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden flex flex-col">
          {/* Stream Header & Category Tabs */}
          <div className="p-4 lg:p-5 border-b border-gray-100 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="text-sm lg:text-base font-bold text-gray-900 dark:text-gray-100">
                  Live Activity Monitoring Stream
                </h3>
                <p className="text-xs text-gray-500">Real-time audit log across all user activities</p>
              </div>

              {/* Filter Tabs */}
              <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0 scrollbar-hide">
                {[
                  { id: "all", label: "All" },
                  { id: "transactions", label: "Coins" },
                  { id: "calls", label: "Calls" },
                  { id: "gifts", label: "Gifts" },
                  { id: "kyc_withdrawals", label: "KYC / Payouts" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActivityTab(tab.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      activityTab === tab.id
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Activity List */}
          <div className="divide-y divide-gray-100 dark:divide-gray-700/60 max-h-[440px] overflow-y-auto custom-scrollbar flex-1">
            {loading ? (
              <div className="py-12 text-center text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-500" />
                <span className="text-xs">Loading activity stream...</span>
              </div>
            ) : filteredActivities.length === 0 ? (
              <div className="py-12 text-center text-xs text-gray-400">
                No activities found for this filter
              </div>
            ) : (
              filteredActivities.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3.5 lg:px-5 hover:bg-gray-50/80 dark:hover:bg-slate-700/30 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2.5 rounded-xl ${item.iconBg} flex-shrink-0`}>
                      <item.icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate">
                        {item.title}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {item.subtitle}
                      </p>
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0 ml-3">
                    <span
                      className={`inline-block text-xs font-bold ${
                        item.amountType === "coins"
                          ? "text-purple-600 dark:text-purple-400"
                          : item.amountType === "money"
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {item.amount}
                    </span>
                    <p className="text-[10px] text-gray-400 mt-0.5 flex items-center justify-end gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {toRelativeTime(item.createdAt)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Bottom Section: Top Performers / Leaderboard & Recent Users */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
        {/* Leaderboard Table (Top Earners vs Top Balance) */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="flex items-center justify-between p-4 lg:p-5 border-b border-gray-100 dark:border-gray-700">
            <div>
              <h3 className="text-sm lg:text-base font-bold text-gray-900 dark:text-gray-100">
                Top Platform Creators & Users
              </h3>
              <p className="text-xs text-gray-500">Highest earning and coin holding users</p>
            </div>

            {/* Toggle Tabs */}
            <div className="inline-flex p-0.5 rounded-lg bg-gray-100 dark:bg-slate-700 text-xs">
              <button
                onClick={() => setLeaderboardTab("earners")}
                className={`px-2.5 py-1 rounded-md font-semibold transition-all cursor-pointer ${
                  leaderboardTab === "earners"
                    ? "bg-white dark:bg-slate-800 text-blue-600 shadow-xs"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                Top Earners
              </button>
              <button
                onClick={() => setLeaderboardTab("balance")}
                className={`px-2.5 py-1 rounded-md font-semibold transition-all cursor-pointer ${
                  leaderboardTab === "balance"
                    ? "bg-white dark:bg-slate-800 text-blue-600 shadow-xs"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                Top Balances
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] uppercase font-bold text-gray-400 bg-gray-50 dark:bg-slate-700/40 border-b border-gray-100 dark:border-gray-700">
                  <th className="px-4 py-2.5">Rank</th>
                  <th className="px-4 py-2.5">User</th>
                  <th className="px-4 py-2.5 text-right">Balance</th>
                  <th className="px-4 py-2.5 text-right">
                    {leaderboardTab === "earners" ? "Lifetime Earned" : "Lifetime Spent"}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60 text-xs">
                {leaderboardUsers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-gray-400">
                      No user records available
                    </td>
                  </tr>
                ) : (
                  leaderboardUsers.map((user, idx) => (
                    <tr
                      key={user._id}
                      className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span
                          className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold ${
                            idx === 0
                              ? "bg-amber-400 text-slate-900 shadow-sm"
                              : idx === 1
                              ? "bg-slate-300 text-slate-900"
                              : idx === 2
                              ? "bg-amber-700 text-white"
                              : "text-gray-400"
                          }`}
                        >
                          {idx + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-[11px] flex-shrink-0">
                            {user.name ? user.name[0].toUpperCase() : "U"}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                              {user.name || "Anonymous User"}
                            </p>
                            <p className="text-[10px] text-gray-400 truncate">
                              @{user.username || "user"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800 dark:text-gray-200">
                        {compact(user.wallet?.balanceCoins || 0)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-600 dark:text-emerald-400">
                        {leaderboardTab === "earners"
                          ? `+${compact(user.wallet?.lifetimeEarned || 0)}`
                          : `-${compact(user.wallet?.lifetimeSpent || 0)}`}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Registered Users */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="flex items-center justify-between p-4 lg:p-5 border-b border-gray-100 dark:border-gray-700">
            <div>
              <h3 className="text-sm lg:text-base font-bold text-gray-900 dark:text-gray-100">
                Recently Registered Users
              </h3>
              <p className="text-xs text-gray-500">New members onboarding to the platform</p>
            </div>
            <NavLink
              to="/admin/users"
              className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1"
            >
              View All Users <ChevronRight className="w-3.5 h-3.5" />
            </NavLink>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-700/60 max-h-[340px] overflow-y-auto custom-scrollbar">
            {(overview?.recentUsers || []).length === 0 ? (
              <div className="py-8 text-center text-xs text-gray-400">No users found</div>
            ) : (
              (overview?.recentUsers || []).map((user) => (
                <div
                  key={user._id}
                  className="flex items-center justify-between p-3.5 px-4 lg:px-5 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                      {user.name ? user.name[0].toUpperCase() : "U"}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {user.name || "User"}
                      </p>
                      <p className="text-[11px] text-gray-400 truncate">@{user.username || "-"}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-right">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        !user.isBlocked
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                          : "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400"
                      }`}
                    >
                      {!user.isBlocked ? "Active" : "Blocked"}
                    </span>
                    <span className="text-[10px] text-gray-400 hidden sm:inline">
                      {toRelativeTime(user.createdAt)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
